import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiArrowLeft,
  FiGlobe,
  FiMail,
  FiPhone,
  FiMapPin,
  FiFileText,
  FiBriefcase,
  FiCheckCircle,
  FiShield,
  FiCheck,
  FiX,
  FiRefreshCw,
} from "react-icons/fi";

import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./ViewTenantDetails.css";

import {
  CollapsibleSection,
  DetailItem,
  StatusBadge,
  LegalEntitiesTable,
  TaxRegistrationsTable,
  LocationsGrid,
  RepresentativesGrid,
  DocumentsGrid,
  LegalAcceptancesList,
  OperationsTable,
} from "./ViewTenantCompo.js";

// Decision Modal Component - Simplified version with only reason
const DecisionModal = ({ isOpen, onClose, onConfirm, decisionType, tenantName, loading }) => {
  const [reason, setReason] = useState("");

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reason.trim()) {
      Swal.fire("Error", "Please enter a reason for this decision.", "error");
      return;
    }
    onConfirm({
      status: decisionType.toUpperCase(),
      reason: reason.trim(),
      risk_classification: "STANDARD",
      next_review_date: null,
    });
  };

  const getDecisionColor = () => {
    switch (decisionType) {
      case "approved": return "#10b981";
      case "rejected": return "#ef4444";
      case "returned": return "#f59e0b";
      default: return "#6366f1";
    }
  };

  const getDecisionIcon = () => {
    switch (decisionType) {
      case "approved": return <FiCheck size={24} />;
      case "rejected": return <FiX size={24} />;
      case "returned": return <FiRefreshCw size={24} />;
      default: return null;
    }
  };

  const getModalTitle = () => {
    switch (decisionType) {
      case "approved": return "Approve Tenant";
      case "rejected": return "Reject Tenant";
      case "returned": return "Return for Revision";
      default: return "Decision";
    }
  };

  const getModalDescription = () => {
    switch (decisionType) {
      case "approved": return "Confirm approval of this tenant application.";
      case "rejected": return "Provide the reason for rejecting this tenant application.";
      case "returned": return "Provide feedback on what needs to be revised.";
      default: return "";
    }
  };

  const getPlaceholder = () => {
    switch (decisionType) {
      case "approved": 
        return "e.g., All KYB documents verified, legal entity confirmed, no red flags.";
      case "rejected": 
        return "e.g., Business registration certificate could not be verified against the government registry.";
      case "returned": 
        return "e.g., Please re-upload a clearer scan of the authorised representative's ID and confirm the registered office address.";
      default: 
        return "Enter your reason...";
    }
  };

  return (
    <div className="vt-modal-overlay" onClick={onClose}>
      <div className="vt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vt-modal-header" style={{ borderLeftColor: getDecisionColor() }}>
          <div className="vt-modal-header-left">
            <div className="vt-modal-icon" style={{ backgroundColor: getDecisionColor() + "20", color: getDecisionColor() }}>
              {getDecisionIcon()}
            </div>
            <div>
              <h3>{getModalTitle()}</h3>
              <p>{getModalDescription()}</p>
            </div>
          </div>
          <button className="vt-modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="vt-modal-body">
            <div className="vt-modal-info">
              <span>Tenant</span>
              <strong>{tenantName}</strong>
            </div>

            <div className="vt-modal-field">
              <label htmlFor="reason">
                Reason / Feedback <span className="vt-required">*</span>
              </label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={getPlaceholder()}
                rows={5}
                required
                autoFocus
              />
            </div>
          </div>

          <div className="vt-modal-footer">
            <button type="button" className="vt-btn vt-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="vt-btn" 
              style={{ 
                background: `linear-gradient(135deg, ${getDecisionColor()} 0%, ${getDecisionColor()}dd 100%)`,
                border: `1px solid ${getDecisionColor()}`,
                color: '#ffffff'
              }}
              disabled={loading}
            >
              {loading ? "Processing..." : (
                <>
                  {getDecisionIcon()}
                  Confirm {decisionType.charAt(0).toUpperCase() + decisionType.slice(1)}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ViewTenantPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [tenantData, setTenantData] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [decisionType, setDecisionType] = useState(null);

  useEffect(() => {
    fetchTenantDetails();
    fetchVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

const fetchTenantDetails = async () => {
  setLoading(true);

  
  console.log("Tenant ID:", id);
 

  try {
    const res = await axios.get(
      `${API_BASE}/tenants/${id}/stage1-details/`
    );

    console.log("Stage 1 API Response:", res);
    console.log("Stage 1 Response Data:", res.data);
    console.log("Stage 1 Success:", res.data?.success);
    console.log("Stage 1 Tenant Details:", res.data?.data);

    if (res.data?.success) {
      setTenantData(res.data.data);
    } else {
      console.error("Stage 1 API returned success=false:", res.data);

      Swal.fire(
        "Error",
        "Unable to fetch tenant details.",
        "error"
      );
    }
  } catch (error) {
    console.error("========== Stage 1 API Error ==========");
    console.error("Tenant ID:", id);
    console.error("Error:", error);
    console.error("Error Response:", error.response);
    console.error("Error Response Data:", error.response?.data);

    Swal.fire(
      "Error",
      error.response?.data?.message ||
        "Failed to fetch tenant details.",
      "error"
    );
  } finally {
    setLoading(false);
  }
};

const fetchVerificationStatus = async () => {
  try {
    const res = await axios.get(`${API_BASE}/tenant-verifications/`);

    console.log("Tenant Verifications API Response:", res);
    console.log("Tenant Verifications Data:", res.data?.data);

    if (res.data?.success && Array.isArray(res.data?.data)) {
      // Filter verification records for the current tenant
      const tenantVerifications = res.data.data.filter(
        (verification) => Number(verification.tenant) === Number(id)
      );

      console.log("Current Tenant ID:", id);
      console.log(
        "Filtered Tenant Verifications:",
        tenantVerifications
      );

      if (tenantVerifications.length > 0) {
        // Sort by created_at - latest record first
        const latestVerification = [...tenantVerifications].sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        )[0];

        console.log(
          "Latest Tenant Verification:",
          latestVerification
        );

        setVerificationStatus(latestVerification);
      } else {
        console.log("No verification records found for tenant:", id);
        setVerificationStatus(null);
      }
    } else {
      console.log("No verification data available.");
      setVerificationStatus(null);
    }
  } catch (error) {
    console.error("Error fetching tenant verification status:", error);
    console.error("Verification API Error Response:", error.response?.data);

    setVerificationStatus(null);
  }
};

  const handleDecisionClick = (type) => {
    setDecisionType(type);
    setModalOpen(true);
  };

  const handleDecisionConfirm = async (payload) => {
    setDecisionLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/tenants/${id}/review-decision/`,
        payload
      );

      if (response.data?.success) {
        Swal.fire(
          "Success",
          `Tenant ${payload.status.toLowerCase()} successfully!`,
          "success"
        );
        setModalOpen(false);
        // Refresh tenant data
        await fetchTenantDetails();
        // Refresh verification status
        await fetchVerificationStatus();
      } else {
        Swal.fire("Error", response.data?.message || "Failed to process decision.", "error");
      }
    } catch (error) {
      console.error("Error processing decision:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to process decision. Please try again.",
        "error"
      );
    } finally {
      setDecisionLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Check if decision buttons should be shown
  const shouldShowDecisionButtons = () => {
    // Don't show buttons if tenant data is not available
    if (!tenantData?.tenant) return false;
    
    // Get the tenant status
    const tenantStatus = tenantData.tenant.status;
    
    // Define final statuses where buttons should be hidden
    const finalStatuses = ["APPROVED", "REJECTED", "RETURNED"];
    
    // Hide buttons if tenant status is in final statuses
    if (finalStatuses.includes(tenantStatus)) {
      return false;
    }
    
    // Also check verification status if available
    if (verificationStatus) {
      const verificationStatusValue = verificationStatus.status;
      if (finalStatuses.includes(verificationStatusValue)) {
        return false;
      }
    }
    
    // Show buttons for pending states
    return true;
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="vt-loader-container">
            <div className="vt-loader"></div>
            <p>Loading tenant details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tenantData?.tenant) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="vt-empty">
            <FiFileText />
            <h3>Tenant details not found</h3>
            <button className="vt-btn vt-btn-secondary" onClick={() => navigate("/superadmin/tenants")}>
              <FiArrowLeft />
              Back to Tenants
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    tenant,
    legal_entities = [],
    tax_registrations = [],
    locations = [],
    authorised_representatives = [],
    documents = [],
    legal_acceptances = [],
    operations = [],
    users=[],
  } = tenantData;

  // Get the current tenant status for display
  const currentStatus = verificationStatus?.status || tenant.status;
  const adminUser = users?.[0] || null;

  // Check if we should show buttons
  const showDecisionButtons = shouldShowDecisionButtons();

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="page-content vt-page">
          {/* Page Header */}
          <div className="vt-page-header">
            <div className="vt-header-left">
              <button
                className="vt-back-icon-btn"
                onClick={() => navigate("/superadmin/tenants/tenant-table")}
                title="Back"
              >
                <FiArrowLeft />
              </button>

              <div>
                
                <h2>{tenant.name} Details</h2>
                
              </div>
            </div>

            <div className="vt-header-actions">
              <StatusBadge status={currentStatus} />
              {/* Decision Buttons - Conditionally rendered based on status */}
              {showDecisionButtons && (
                <div className="vt-decision-buttons">
                  <button
                    className="vt-btn vt-btn-approve"
                    onClick={() => handleDecisionClick("approved")}
                  >
                    <FiCheck />
                    Approve
                  </button>
                  <button
                    className="vt-btn vt-btn-reject"
                    onClick={() => handleDecisionClick("rejected")}
                  >
                    <FiX />
                    Reject
                  </button>
                  <button
                    className="vt-btn vt-btn-return"
                    onClick={() => handleDecisionClick("returned")}
                  >
                    <FiRefreshCw />
                    Return
                  </button>
                </div>
              )}
            </div>
          </div>

          
          {/* Verification Status Info */}
{verificationStatus && (
  <div className="vt-verification-info">
    <div className="vt-verification-item">
      <span>Verification Status:</span>
      <StatusBadge status={verificationStatus.status} />
    </div>

    {verificationStatus.reason && (
      <div className="vt-verification-item">
        <span>Reason:</span>
        <p>{verificationStatus.reason}</p>
      </div>
    )}

    {verificationStatus.reviewed_at && (
      <div className="vt-verification-item">
        <span>Reviewed At:</span>
        <p>{formatDateTime(verificationStatus.reviewed_at)}</p>
      </div>
    )}
  </div>
)}

          {/* Overview */}
<div className="vt-overview-card">
  <div className="vt-profile">
    <div className="vt-logo">
      {tenant.logo ? (
        <img
          src={
            tenant.logo.startsWith("http")
              ? tenant.logo
              : `${API_BASE.replace(/\/api\/?$/, "")}${tenant.logo}`
          }
          alt={tenant.name}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      ) : (
        <span>{tenant.name?.charAt(0).toUpperCase()}</span>
      )}
    </div>

    <div className="vt-profile-info">
      <h1>{tenant.name}</h1>

      <div className="vt-admin-contact">
        <div>
          <FiMail />
          <span>{adminUser?.email || "-"}</span>
        </div>

        <div>
          <FiPhone />
          <span>
            {adminUser
              ? `${adminUser.mobile_country_code || "+91"} ${
                  adminUser.mobile_number || ""
                }`.trim()
              : "-"}
          </span>
        </div>
      </div>
    </div>
  </div>

  <div className="vt-overview-details">
    <div>
      <span>Workspace Type</span>
      <strong>{tenant.workspace_type || "-"}</strong>
    </div>

    <div>
      <span>Organisation Type</span>
      <strong>{tenant.organisation_type || "-"}</strong>
    </div>

    <div>
      <span>Currency</span>
      <strong>{tenant.default_currency || "-"}</strong>
    </div>

    <div>
      <span>Timezone</span>
      <strong>{tenant.default_timezone || "-"}</strong>
    </div>
  </div>
</div>

          {/* Basic Information */}
          <CollapsibleSection icon={<FiFileText />} title="Basic Information" accent="indigo">
            <div className="vt-details-grid">
              <DetailItem label="Legal Name" value={tenant.legal_name} />
              <DetailItem label="Trade Name" value={tenant.trade_name} />
              <DetailItem label="Tenant Code" value={tenant.code} />
              <DetailItem label="Contact Email" value={tenant.contact_email} />
              <DetailItem label="Contact Phone" value={tenant.contact_phone} />
              <DetailItem label="Portal Slug" value={tenant.portal_slug} />
              <DetailItem label="Custom Domain" value={tenant.custom_domain} />
              <DetailItem
                label="Industry IDs"
                value={tenant.industry_ids?.length ? tenant.industry_ids.join(", ") : "-"}
              />
              <DetailItem
                label="Service Scope IDs"
                value={tenant.service_scope_ids?.length ? tenant.service_scope_ids.join(", ") : "-"}
              />
              
            </div>

            {tenant.website && (
              <div className="vt-link-row">
                <FiGlobe />
                <a href={tenant.website} target="_blank" rel="noopener noreferrer">
                  {tenant.website}
                </a>
              </div>
            )}

            <div className="vt-description">
              <span>Description</span>
              <p>{tenant.description || "-"}</p>
            </div>
          </CollapsibleSection>

          {/* Legal Entities */}
          <CollapsibleSection
            icon={<FiBriefcase />}
            title="Legal Entities"
            count={legal_entities.length}
            accent="violet"
            defaultOpen={false}
          >
            <LegalEntitiesTable
              entities={legal_entities}
              formatDate={formatDate}
              formatDateTime={formatDateTime}
            />
          </CollapsibleSection>

          {/* Tax Registrations */}
          <CollapsibleSection
            icon={<FiFileText />}
            title="Tax Registrations"
            count={tax_registrations.length}
            accent="amber"
            defaultOpen={false}
          >
            <TaxRegistrationsTable taxRegistrations={tax_registrations} />
          </CollapsibleSection>

          {/* Locations */}
          <CollapsibleSection
            icon={<FiMapPin />}
            title="Locations"
            count={locations.length}
            accent="rose"
            defaultOpen={false}
          >
            <LocationsGrid locations={locations} />
          </CollapsibleSection>

          {/* Authorised Representatives */}
          <CollapsibleSection
            icon={<FiShield />}
            title="Authorised Representatives"
            count={authorised_representatives.length}
            accent="cyan"
            defaultOpen={false}
          >
            <RepresentativesGrid representatives={authorised_representatives} formatDate={formatDate} />
          </CollapsibleSection>

          {/* Documents */}
          <CollapsibleSection
            icon={<FiFileText />}
            title="Documents"
            count={documents.length}
            accent="emerald"
            defaultOpen={false}
          >
            <DocumentsGrid documents={documents} formatDate={formatDate} />
          </CollapsibleSection>

          {/* Legal Acceptances */}
          <CollapsibleSection
            icon={<FiCheckCircle />}
            title="Legal Acceptances"
            count={legal_acceptances.length}
            accent="fuchsia"
            defaultOpen={false}
          >
            <LegalAcceptancesList acceptances={legal_acceptances} formatDateTime={formatDateTime} />
          </CollapsibleSection>

          {/* Operations */}
          <CollapsibleSection
            icon={<FiGlobe />}
            title="Operations"
            count={operations.length}
            accent="blue"
            defaultOpen={false}
          >
            <OperationsTable operations={operations} formatDate={formatDate} />
          </CollapsibleSection>

          {/* Bottom Actions */}
          {/* <div className="vt-bottom-actions">
            <button
              className="vt-btn vt-btn-secondary"
              onClick={() => navigate("/superadmin/tenants/tenant-table")}
            >
              <FiArrowLeft />
              Back to Tenants
            </button>

            <button className="vt-btn vt-btn-primary">Edit Tenant</button>
          </div> */}
        </div>
      </div>

      {/* Decision Modal */}
      <DecisionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleDecisionConfirm}
        decisionType={decisionType}
        tenantName={tenant.name}
        loading={decisionLoading}
      />
    </div>
  );
};

export default ViewTenantPage;